import React, { useContext } from "react";
import { styled } from "@mui/material/styles";
import { Box, Button, Container, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import DefaultLayout from "../layouts/DefaultLayout";
import { AccountContext } from "../store/AccountContext";
import Seo from "./common/Seo";
import TvAppPromo from "./common/TvAppPromo";
import appleappstore from "./Modals/app-store-logo.png";
import googleplaystore from "./Modals/google-play-logo.png";

// /link-changed (#888): where an episode link lands when the episode can't
// be found, usually because its slug was changed after the link went out.
// Replaces the bare 404 block on the preview page. Store URLs and badge
// images are the direct ones the shared install block uses
// (common/InstallApp/links.ts); styling follows that block's footer layout
// (common/InstallApp/FooterLayout.tsx). Every text colour is named
// explicitly: MUI's defaults assume a light background (CLAUDE.md, Styling).

const APP_STORE_URL = "https://apps.apple.com/us/app/farmflix/id1490609587";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.farmflix&hl=en_GB&gl=US";

const Background = styled("div")(({ theme }) => ({
  width: "100%",
  flexGrow: 1,
  backgroundColor: theme.palette.background.ffNavy,
  textAlign: "center",
}));

const AppStoreLogo = styled("img")(({ theme }) => ({
  maxWidth: "140px",
  margin: "10px 5px",
  [theme.breakpoints.up("md")]: {
    maxWidth: "160px",
  },
}));

const GreenLink = styled(Link)(({ theme }) => ({
  color: theme.palette.background.ffGreen,
}));

const titleSx = {
  color: "common.white",
  fontSize: "1.8rem",
  mb: 2,
  "@media (min-width:600px)": { fontSize: "2.4rem" },
};
const bodySx = { color: "common.white", fontSize: "1.1rem", mb: 1 };
const ctaSx = {
  color: "background.ffGreen",
  fontSize: "1.2rem",
  fontWeight: 500,
  mt: 3,
  mb: 1,
};
const buttonSx = { color: "common.black", mt: 3, mb: 1, px: 5, py: 1.5 };
const secondarySx = { color: "common.white", mt: 2 };

const LinkChanged = () => {
  const { loggedIn } = useContext(AccountContext);

  // DefaultLayout for everyone (#894): LoggedInLayout sends expired, locked
  // and incomplete members to their account page, and this page is for
  // anyone who followed an old link. DefaultLayout already shows the member
  // header when someone is logged in.
  return (
    <DefaultLayout>
      <Seo
        title="This link has changed"
        description="The episode this link pointed to has moved. Get the FarmFLiX app and search our catalogue to find it."
        path="/link-changed"
      />
      <Background>
        <Container maxWidth="sm">
          <Box sx={{ my: 10 }}>
            <Typography variant="h1" sx={titleSx}>
              Oops, this link has changed
            </Typography>
            <Typography sx={bodySx}>
              We couldn&apos;t find the episode this link pointed to. It may
              have been renamed or moved.
            </Typography>

            {loggedIn ? (
              <Link to="/search" style={{ textDecoration: "none" }}>
                <Button size="large" sx={buttonSx}>
                  Search the catalogue
                </Button>
              </Link>
            ) : (
              <>
                <Typography sx={ctaSx}>
                  Get the FarmFLiX app and search our catalogue to find it.
                </Typography>
                <div>
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <AppStoreLogo
                      src={appleappstore}
                      alt="Download on the App Store"
                    />
                  </a>
                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <AppStoreLogo
                      src={googleplaystore}
                      alt="Get it on Google Play"
                    />
                  </a>
                </div>
                <Typography sx={secondarySx}>
                  Already a member? <GreenLink to="/login">Log in</GreenLink>
                </Typography>
              </>
            )}

            <Box sx={{ mt: 6, textAlign: "left" }}>
              <TvAppPromo />
            </Box>
          </Box>
        </Container>
      </Background>
    </DefaultLayout>
  );
};

export default LinkChanged;
